import React, { useEffect, useState, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import api from '../../services/api';

// Hücre içi seviye editor'ü: local state + save butonu
const LevelEditorCell = React.memo(function LevelEditorCell({ row, value, onSave }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => {
    setLocal(value ?? '');
  }, [value, row?.skill_id]);

  return (
    <div className="flex gap-2" key={row.skill_id}>
      <InputText
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder="Seviye"
        style={{ width: 100 }}
      />
      <Button
        icon="pi pi-save"
        className="p-button-sm p-button-success"
        onClick={() => onSave(local)}
      />
    </div>
  );
});

export default function SkillManagement() {
  // Master data
  const [hierarchies, setHierarchies] = useState([]);
  const [skills, setSkills] = useState([]);
  const [users, setUsers] = useState([]);

  // User-focused state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSkills, setUserSkills] = useState([]);
  const [missingSkills, setMissingSkills] = useState([]);
  const [extraSkills, setExtraSkills] = useState([]);

  // Kullanıcıya atanmış hiyerarşiler (modal listesi)
  const [userHierarchies, setUserHierarchies] = useState([]);

  // Assign skill to user
  const [selectedUserSkill, setSelectedUserSkill] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('');

  // User-hierarchy assignment (modal)
  const [selectedUserHierarchy, setSelectedUserHierarchy] = useState(null);
  const [showAssignHierarchyModal, setShowAssignHierarchyModal] = useState(false);

  // MODALS (yönetim)
  const [showHierarchyModal, setShowHierarchyModal] = useState(false);
  const [showSkillModal, setShowSkillModal] = useState(false);

  // Forms (modal içi)
  const [newHierarchyName, setNewHierarchyName] = useState('');
  const [newSkillName, setNewSkillName] = useState('');
  const [selectedHierarchyForNewSkill, setSelectedHierarchyForNewSkill] = useState(null);
  const [selectedParentSkill, setSelectedParentSkill] = useState(null);

  // Inline editor cache
  const [levelInputs, setLevelInputs] = useState({}); // { [skill_id]: "value" }

  // === Fetchers ===
  const fetchHierarchies = async () => {
    const res = await api.get('/skill-hierarchies');
    setHierarchies(res.data);
  };

  const fetchSkills = async () => {
    const res = await api.get('/skills');
    setSkills(res.data);
  };

  const fetchUsers = async () => {
    const res = await api.get('/users');
    setUsers(res.data);
  };

  const fetchUserSkills = async (userId) => {
    const res = await api.get(`/user-skills/${userId}`);
    setUserSkills(res.data);
    const next = {};
    (res.data || []).forEach((row) => (next[row.skill_id] = String(row.level ?? '')));
    setLevelInputs(next);
  };

  const fetchMissingSkills = async (userId) => {
    try {
      const res = await api.get(`/users/${userId}/missing-skills`);
      setMissingSkills(res.data);
    } catch {
      setMissingSkills([]);
    }
  };

  const fetchExtraSkills = async (userId) => {
    try {
      const res = await api.get(`/users/${userId}/extra-skills`);
      setExtraSkills(res.data);
    } catch {
      setExtraSkills([]);
    }
  };

  // Kullanıcıya atanmış hiyerarşiler: **backend'de GET endpoint yok** → userSkills'ten türet
  const buildUserHierarchiesFromUserSkills = () => {
    const map = new Map();
    (userSkills || []).forEach((us) => {
      const h = us?.skill?.hierarchy;
      if (h?.id && h?.name && !map.has(h.id)) {
        map.set(h.id, { id: h.id, name: h.name });
      }
    });
    setUserHierarchies(Array.from(map.values()));
  };

  useEffect(() => {
    // İlk açılışta sadece kullanıcılar görünsün
    fetchUsers();
  }, []);

  // selectedUser veya userSkills değişince atanmış hiyerarşileri türet
  useEffect(() => {
    if (selectedUser?.id) {
      buildUserHierarchiesFromUserSkills();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.id, userSkills]);

  // === Mutations ===
  const handleCreateHierarchy = async () => {
    if (!newHierarchyName.trim()) return;
    await api.post('/skill-hierarchies', { name: newHierarchyName.trim() });
    setNewHierarchyName('');
    await fetchHierarchies();
  };

  const handleCreateSkill = async () => {
    if (!newSkillName.trim() || !selectedHierarchyForNewSkill) return;
    await api.post('/skills', {
      name: newSkillName.trim(),
      hierarchy_id: selectedHierarchyForNewSkill.id,
      parent_id: selectedParentSkill ? selectedParentSkill.id : null
    });
    setNewSkillName('');
    setSelectedParentSkill(null);
    await fetchSkills();
  };

  const handleAssignSkill = async () => {
    if (!selectedUser || !selectedUserSkill || selectedLevel === '' || isNaN(Number(selectedLevel))) return;
    await api.post('/user-skills', {
      user_id: selectedUser.id,
      skill_id: selectedUserSkill.id,
      level: Number(selectedLevel)
    });
    setSelectedUserSkill(null);
    setSelectedLevel('');
    await fetchUserSkills(selectedUser.id);
    await fetchMissingSkills(selectedUser.id);
    await fetchExtraSkills(selectedUser.id);
  };

  const handleUpdateLevel = async (skillId, newLevel) => {
    if (!selectedUser) return;
    if (newLevel === '' || isNaN(Number(newLevel))) return;
    await api.put(`/user-skills/${selectedUser.id}/${skillId}`, { level: Number(newLevel) });
    await fetchUserSkills(selectedUser.id);
    await fetchMissingSkills(selectedUser.id);
    await fetchExtraSkills(selectedUser.id);
  };

  const handleDeleteUserSkill = async (skillId) => {
    if (!selectedUser) return;
    await api.delete(`/user-skills/${selectedUser.id}/${skillId}`);
    await fetchUserSkills(selectedUser.id);
    await fetchMissingSkills(selectedUser.id);
    await fetchExtraSkills(selectedUser.id);
  };

  const handleAttachHierarchy = async () => {
    if (!selectedUser || !selectedUserHierarchy) return;
    await api.post(`/users/${selectedUser.id}/skill-hierarchies/${selectedUserHierarchy.id}`);
    setSelectedUserHierarchy(null);
    await fetchMissingSkills(selectedUser.id);
    await fetchExtraSkills(selectedUser.id);
    // Hiyerarşiler GET edilemediği için, listede görünmesi için en yakın etki: yeniden türet
    buildUserHierarchiesFromUserSkills();
  };

  const handleDetachHierarchy = async (hierarchyId) => {
    if (!selectedUser || !hierarchyId) return;
    await api.delete(`/users/${selectedUser.id}/skill-hierarchies/${hierarchyId}`);
    await fetchMissingSkills(selectedUser.id);
    await fetchExtraSkills(selectedUser.id);
    buildUserHierarchiesFromUserSkills();
  };

  const onUserRowClick = async (e) => {
    setSelectedUser(e.data);
    setSelectedUserSkill(null);
    setSelectedLevel('');
    setSelectedUserHierarchy(null);
    // Skill dropdown boş kalmasın diye burada da skills çekiyoruz
    if (!skills?.length) await fetchSkills();

    await Promise.all([
      fetchUserSkills(e.data.id),
      fetchMissingSkills(e.data.id),
      fetchExtraSkills(e.data.id),
    ]);
    buildUserHierarchiesFromUserSkills();
  };

  // === Modal aç/kapa ===
  const openHierarchyModal = async () => {
    if (hierarchies.length === 0) await fetchHierarchies();
    setShowHierarchyModal(true);
  };
  const openSkillModal = async () => {
    if (hierarchies.length === 0) await fetchHierarchies();
    if (skills.length === 0) await fetchSkills();
    setShowSkillModal(true);
  };
  const openAssignHierarchyModal = async () => {
    if (hierarchies.length === 0) await fetchHierarchies();
    buildUserHierarchiesFromUserSkills();
    setShowAssignHierarchyModal(true);
  };

  // === Column bodies ===
  const levelEditorBody = (row) => {
    const initVal = levelInputs[row.skill_id] ?? String(row.level ?? '');
    return (
      <LevelEditorCell
        row={row}
        value={initVal}
        onSave={(val) => handleUpdateLevel(row.skill_id, val)}
      />
    );
  };

  const actionsBody = (row) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-danger"
        onClick={() => handleDeleteUserSkill(row.skill_id)}
        tooltip="Sil"
      />
    </div>
  );

  // Kullanıcıda olmayan hiyerarşileri ekleme dropdown'unda göster
  const addableHierarchyOptions = useMemo(() => {
    const assignedIds = new Set((userHierarchies || []).map(h => h.id));
    return (hierarchies || []).filter(h => !assignedIds.has(h.id));
  }, [hierarchies, userHierarchies]);

  // === Render ===
  return (
    <div className="p-3 flex flex-column gap-5">
      {/* Üstte modal açan butonlar */}
      <div className="flex gap-2">
        <Button label="Hiyerarşiler" icon="pi pi-sitemap" onClick={openHierarchyModal} />
        <Button label="Skiller" icon="pi pi-list" onClick={openSkillModal} />
      </div>

      {/* Users — tek görünür kısım başlangıçta */}
      <section>
        <h3>Kullanıcılar</h3>
        <DataTable
          value={users}
          paginator
          rows={10}
          stripedRows
          selectionMode="single"
          onRowClick={onUserRowClick}
          dataKey="id"
        >
          <Column field="first_name" header="Ad" filter filterPlaceholder="Ara..." />
          <Column field="last_name" header="Soyad" filter filterPlaceholder="Ara..." />
          <Column field="email" header="Email" filter filterPlaceholder="Ara..." />
        </DataTable>
      </section>

      {/* User detail */}
      {selectedUser && (
        <section className="flex flex-column gap-4">
          <div className="flex items-center justify-between">
            <h4>{selectedUser.first_name} {selectedUser.last_name}</h4>
            {/* Tek butonla hiyerarşi ata/çıkar modalı */}
            <Button
              label="Hiyerarşi Ata/Çıkar"
              icon="pi pi-sitemap"
              onClick={openAssignHierarchyModal}
            />
          </div>

          {/* Eksik Skiller — SADECE varsa göster */}
          {missingSkills?.length > 0 && (
            <div className="flex flex-column gap-2">
              <h5>Eksik Skiller (Assigned hiyerarşilere göre)</h5>
              <DataTable value={missingSkills} paginator rows={5} stripedRows emptyMessage="Eksik skill bulunamadı" dataKey="id">
                <Column field="name" header="Skill" filter filterPlaceholder="Ara..." />
                <Column field="hierarchy.name" header="Hiyerarşi" filter filterPlaceholder="Ara..." />
                <Column
                  header="Hızlı Ata"
                  body={(row) => (
                    <div className="flex gap-2">
                      <InputText
                        placeholder="Seviye"
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter') {
                            const val = Number(e.target.value);
                            if (!isNaN(val)) {
                              await api.post('/user-skills', {
                                user_id: selectedUser.id,
                                skill_id: row.id,
                                level: val
                              });
                              e.target.value = '';
                              await fetchUserSkills(selectedUser.id);
                              await fetchMissingSkills(selectedUser.id);
                              await fetchExtraSkills(selectedUser.id);
                            }
                          }
                        }}
                        style={{ width: 100 }}
                      />
                      <Button
                        icon="pi pi-check"
                        className="p-button-sm"
                        onClick={async () => {
                          const valStr = prompt('Seviye?');
                          if (valStr === null) return;
                          const val = Number(valStr);
                          if (!isNaN(val)) {
                            await api.post('/user-skills', {
                              user_id: selectedUser.id,
                              skill_id: row.id,
                              level: val
                            });
                            await fetchUserSkills(selectedUser.id);
                            await fetchMissingSkills(selectedUser.id);
                            await fetchExtraSkills(selectedUser.id);
                          }
                        }}
                      />
                    </div>
                  )}
                />
              </DataTable>
            </div>
          )}

          {/* Assign arbitrary skill */}
          <div className="flex flex-column gap-2">
            <h5>Skill Ata</h5>
            <div className="flex flex-wrap gap-2">
              <Dropdown
                value={selectedUserSkill}
                options={skills}
                onChange={(e) => setSelectedUserSkill(e.value)}
                optionLabel="name"
                placeholder="Skill seç"
                filter
                showClear
              />
              <InputText
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                placeholder="Seviye (0-10)"
                style={{ width: 140 }}
              />
              <Button label="Ata" icon="pi pi-check" onClick={handleAssignSkill} />
            </div>
          </div>

          {/* User skills */}
          <div className="flex flex-column gap-2">
            <h5>Kullanıcı Skilleri</h5>
            <DataTable value={userSkills} paginator rows={10} stripedRows emptyMessage="Kayıt yok" dataKey="skill_id">
              <Column field="skill.name" header="Skill" filter filterPlaceholder="Ara..." />
              <Column field="skill.hierarchy.name" header="Hiyerarşi" filter filterPlaceholder="Ara..." />
              <Column field="level" header="Seviye" filter filterPlaceholder="Ara..." />
              <Column header="Seviye Güncelle" body={levelEditorBody} />
              <Column header="İşlem" body={actionsBody} />
            </DataTable>
          </div>

          {/* Extra skills */}
          {extraSkills?.length > 0 && (
            <div className="flex flex-column gap-2">
              <h5>Extra Skiller</h5>
              <DataTable value={extraSkills} paginator rows={5} stripedRows dataKey="skill_id">
                <Column field="skill.name" header="Skill" filter filterPlaceholder="Ara..." />
                <Column field="skill.hierarchy.name" header="Hiyerarşi" filter filterPlaceholder="Ara..." />
                <Column field="level" header="Seviye" filter filterPlaceholder="Ara..." />
                <Column header="Seviye Güncelle" body={(row) => (
                  <LevelEditorCell
                    row={row}
                    value={levelInputs[row.skill_id] ?? String(row.level ?? '')}
                    onSave={(val) => handleUpdateLevel(row.skill_id, val)}
                  />
                )} />
                <Column header="İşlem" body={(row) => actionsBody(row)} />
              </DataTable>
            </div>
          )}
        </section>
      )}

      {/* === Hiyerarşi Modal (liste+ekle) === */}
      <Dialog
        header="Skill Hiyerarşileri"
        visible={showHierarchyModal}
        onHide={() => setShowHierarchyModal(false)}
        style={{ width: '60rem', maxWidth: '95vw' }}
        modal
      >
        <div className="flex flex-column gap-3">
          <div className="flex gap-2">
            <InputText
              value={newHierarchyName}
              onChange={(e) => setNewHierarchyName(e.target.value)}
              placeholder="Yeni hiyerarşi adı"
            />
            <Button label="Ekle" icon="pi pi-plus" onClick={handleCreateHierarchy} />
          </div>

          <DataTable value={hierarchies} paginator rows={8} stripedRows dataKey="id">
            <Column field="id" header="ID" filter filterPlaceholder="Ara..." />
            <Column field="name" header="Hiyerarşi Adı" filter filterPlaceholder="Ara..." />
          </DataTable>
        </div>
      </Dialog>

      {/* === Skill Modal (liste+ekle) === */}
      <Dialog
        header="Skiller"
        visible={showSkillModal}
        onHide={() => setShowSkillModal(false)}
        style={{ width: '70rem', maxWidth: '95vw' }}
        modal
      >
        <div className="flex flex-column gap-3">
          <div className="flex flex-column md:flex-row gap-2">
            <InputText
              value={newSkillName}
              onChange={(e) => setNewSkillName(e.target.value)}
              placeholder="Skill adı"
            />
            <Dropdown
              value={selectedHierarchyForNewSkill}
              options={hierarchies}
              onChange={(e) => setSelectedHierarchyForNewSkill(e.value)}
              optionLabel="name"
              placeholder="Hiyerarşi seç"
              filter
              showClear
              className="w-full md:w-20rem"
            />
            <Dropdown
              value={selectedParentSkill}
              options={
                selectedHierarchyForNewSkill
                  ? skills.filter((s) => s.hierarchy_id === selectedHierarchyForNewSkill.id)
                  : skills
              }
              onChange={(e) => setSelectedParentSkill(e.value)}
              optionLabel="name"
              placeholder="Parent skill (opsiyonel)"
              filter
              showClear
              className="w-full md:w-20rem"
            />
            <Button label="Ekle" icon="pi pi-plus" onClick={handleCreateSkill} />
          </div>

          <DataTable value={skills} paginator rows={10} stripedRows dataKey="id">
            <Column field="id" header="ID" filter filterPlaceholder="Ara..." />
            <Column field="name" header="Skill" filter filterPlaceholder="Ara..." />
            <Column field="hierarchy.name" header="Hiyerarşi" filter filterPlaceholder="Ara..." />
            <Column field="parent_id" header="Parent ID" filter filterPlaceholder="Ara..." />
          </DataTable>
        </div>
      </Dialog>

      {/* === Kullanıcıya Hiyerarşi Ata/Çıkar Modal === */}
      <Dialog
        header="Kullanıcıya Hiyerarşi Ata/Çıkar"
        visible={showAssignHierarchyModal}
        onHide={() => setShowAssignHierarchyModal(false)}
        style={{ width: '50rem', maxWidth: '95vw' }}
        modal
      >
        <div className="flex flex-column gap-3">
          {/* Kullanıcıya atanmış hiyerarşiler */}
          <div>
            <h5 className="mb-2">Atanmış Hiyerarşiler</h5>
            <DataTable value={userHierarchies} stripedRows dataKey="id" rows={5} paginator>
              <Column field="id" header="ID" />
              <Column field="name" header="Ad" />
              <Column
                header="İşlem"
                body={(row) => (
                  <Button
                    icon="pi pi-trash"
                    className="p-button-sm p-button-danger"
                    onClick={() => handleDetachHierarchy(row.id)}
                    tooltip="Kaldır"
                  />
                )}
              />
            </DataTable>
          </div>

          {/* Yeni hiyerarşi ekle */}
          <div className="flex flex-column gap-2">
            <h5>Hiyerarşi Ekle</h5>
            <div className="flex gap-2">
              <Dropdown
                value={selectedUserHierarchy}
                options={addableHierarchyOptions}
                onChange={(e) => setSelectedUserHierarchy(e.value)}
                optionLabel="name"
                placeholder="Hiyerarşi seç"
                filter
                showClear
                className="w-full md:w-20rem"
              />
              <Button
                label="Ekle"
                icon="pi pi-plus"
                onClick={handleAttachHierarchy}
                disabled={!selectedUser || !selectedUserHierarchy}
              />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
